/* eslint no-use-before-define: "off"*/

import Logger from '@KrashidBuilt/common/utils/logger';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import posthog from 'posthog-js';

import { ROUTE_MAP } from '../routes';
import { AUTH_WEB_API_BASE } from '../constants';

const logger = new Logger('credentials.js');

const KEY = 'creds';
const MS_THRESHOLD = 300_000; // 5 minutes
const RF_THRESHOLD = 30_000;

const setSentryContext = () => {
    try {
        const user = getUser();
        if (user) {
            logger.extra('Sentry Scope: Setting', { user });
            const { uId, username, email, firstName, lastName, phone, customer } = user;
            Sentry.setUser({ id: uId, firstName, lastName, phone, username, email });
            Sentry.setContext('customer', customer);
        } else {
            logger.debug('Sentry Scope: Clearing');
            Sentry.configureScope((scope) => scope.clear());
        }
    } catch (error) {
        logger.error('Unable to set user', error);
    }
};

const get = () => {
    try {
        return JSON.parse(localStorage.getItem(KEY));
    } catch (error) { }
};

const set = (o) => {
    localStorage.setItem(KEY, JSON.stringify(o));
    setSentryContext();
};

const remove = () => {
    localStorage.removeItem(KEY);
    setSentryContext();
};


export const hasValidAccessToken = (found, threshold = MS_THRESHOLD) => {
    if (!found) {
        found = get();
    }
    return found && found.access && found.access.expires > (new Date().getTime() + threshold);
};

export const hasValidRefreshToken = (found, threshold = MS_THRESHOLD) => {
    if (!found) {
        found = get();
    }
    return found && found.refresh && found.refresh.expires > (new Date().getTime() + threshold);
};


export const getAccessToken = async () => {
    const found = get();

    if (hasValidAccessToken(found)) {
        return found.access.token;
    }

    if (hasValidRefreshToken(found)) {
        await refresh();

        const refreshed = get();

        if (hasValidAccessToken(refreshed)) {
            return refreshed.access.token;
        }
    }

    logout();
};

export const getRefreshToken = () => {
    const found = get();

    if (hasValidRefreshToken(found)) {
        return found.refresh.token;
    }

    logout();
};

export const getUser = () => {
    const found = get();
    if (found) {
        return found.user;
    }
};

export const login = async ({ username, password, companyId }) => {
    logger.extra({ username, password, companyId });
    try {
        const { data } = await axios({
            method: 'POST',
            url: `${AUTH_WEB_API_BASE}/api/public/login`,
            headers: {
                'Content-Type': 'application/json',
            },
            data: {
                username,
                password,
                companyId
            }
        });

        set(data);

        return data;
    } catch (error) {
        logger.error('Unable to login', error);
        throw error;
    }
};

export const logout = () => {
    remove();
    const { pathname } = window.location;
    const array = pathname.split('/');
    const end = array[array?.length - 1];
    posthog.reset();
    if (['login', 'logout'].indexOf(end) === -1) {
        window.location.href = ROUTE_MAP.LOGOUT;
    }
};

export const refresh = async () => {
    logger.extra();

    try {
        const { data } = await axios({
            method: 'POST',
            url: `${AUTH_WEB_API_BASE}/api/public/refresh`,
            headers: {
                'Content-Type': 'application/json',
            },
            data: {
                token: getRefreshToken()
            }
        });

        set(data);

        return data;
    } catch (error) {
        logger.error('Unable to refresh', error);
    }

    logout();
};


const refreshTask = () => {
    const found = get();

    if (!hasValidAccessToken(found, RF_THRESHOLD * 1.5) && hasValidRefreshToken(found)) {
        refresh();
    }
};

setInterval(refreshTask, RF_THRESHOLD);
setSentryContext();